import React from 'react';
import dayjs from 'dayjs';
import { formatDateWithTimezone } from '../../utilities/formatting';
import { dateTimeFormat } from '../../utilities/constants';
import { type DateValueType } from 'react-tailwindcss-datepicker';
import { type SelectOption } from '../controls/InputSelect';

interface EventTimelineProps {
  buildDateValue: DateValueType;
  eventDateValue: DateValueType;
  deleteDateValue: DateValueType;
  timezone: SelectOption;
}

const EventTimeline: React.FC<EventTimelineProps> = ({ buildDateValue, eventDateValue, deleteDateValue, timezone }) => {
  if (
    !buildDateValue?.startDate ||
    !eventDateValue?.startDate ||
    !eventDateValue?.endDate ||
    !deleteDateValue?.startDate
  ) {
    return null;
  }

  return (
    <div className="bg-dataops-primary-dark-blue/5 p-4 rounded-md mb-4">
      <h3 className="text-sm font-medium text-gray-700 mb-2">Event Timeline (All times in {timezone.name})</h3>
      <div className="relative">
        {/* Build time */}
        <div className="relative pl-10 pb-6">
          <div className="absolute left-2 w-1 mx-[6px] top-0 bottom-0 bg-blue-200"></div>

          <div className="absolute left-2 w-4 h-4 rounded-full bg-blue-500 border-2 border-white"></div>
          <div className="text-sm">
            <span className="font-medium text-blue-700">Build Time</span>
            <div className="text-gray-600">
              {dayjs(formatDateWithTimezone(buildDateValue.startDate, 5, timezone.id))
                .tz(timezone.id)
                .format(dateTimeFormat)}
              <span className="text-xs ml-1 text-gray-500">
                ({formatDateWithTimezone(buildDateValue.startDate, 5, timezone.id)})
              </span>
            </div>
          </div>
        </div>

        {/* Start time */}
        <div className="relative pl-10 pb-6">
          <div className="absolute left-2 w-1 mx-[6px] top-0 bottom-0 bg-blue-200"></div>
          <div className="absolute left-2 w-4 h-4 rounded-full bg-green-500 border-2 border-white"></div>
          <div className="text-sm">
            <span className="font-medium text-green-700">Start Time</span>
            <div className="text-gray-600">
              {dayjs(formatDateWithTimezone(eventDateValue.startDate, 7, timezone.id))
                .tz(timezone.id)
                .format(dateTimeFormat)}
              <span className="text-xs ml-1 text-gray-500">
                ({formatDateWithTimezone(eventDateValue.startDate, 7, timezone.id)})
              </span>
            </div>
          </div>
        </div>

        {/* End time */}
        <div className="relative pl-10 pb-6">
          <div className="absolute left-2 w-1 mx-[6px] top-0 bottom-0 bg-blue-200"></div>
          <div className="absolute left-2 w-4 h-4 rounded-full bg-yellow-500 border-2 border-white"></div>
          <div className="text-sm">
            <span className="font-medium text-yellow-700">End Time</span>
            <div className="text-gray-600">
              {dayjs(formatDateWithTimezone(eventDateValue.endDate, 22, timezone.id))
                .tz(timezone.id)
                .format(dateTimeFormat)}
              <span className="text-xs ml-1 text-gray-500">
                ({formatDateWithTimezone(eventDateValue.endDate, 22, timezone.id)})
              </span>
            </div>
          </div>
        </div>

        {/* Decommission time */}
        <div className="relative pl-10">
          <div className="absolute left-2 w-4 h-4 rounded-full bg-red-500 border-2 border-white"></div>
          <div className="text-sm">
            <span className="font-medium text-red-700">Decommission Time</span>
            <div className="text-gray-600">
              {dayjs(formatDateWithTimezone(deleteDateValue.startDate, 23, timezone.id))
                .tz(timezone.id)
                .format(dateTimeFormat)}
              <span className="text-xs ml-1 text-gray-500">
                ({formatDateWithTimezone(deleteDateValue.startDate, 23, timezone.id)})
              </span>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default EventTimeline;
